import {useMemo, useCallback, useState} from 'react';

import {TreeViewSelection, BasicTreeViewSelectionOptions} from 'filters/features/FilterDialog/types';

import {GenericTreeNode, groupSelectedKeys, groupSelectedKeysByLevel} from 'utils/treeUtils';

import {TreeBasicNode} from 'components/Tree/TreeTypes';
import {selectedByLevelToFilter} from 'components/TreePicker/utils/filterTreePickerUtils';

import {SelectedTooltip} from 'filters/features/FilterDialog/components/SelectedTooltip';
import {CounterTooltip} from 'filters/components/CounterTooltip';

type GroupedByLevelTreeSelectionOptions = BasicTreeViewSelectionOptions & {
  entity?: string;
  levels: Array<{key: string; label: string}>;
};

export const useGroupedByLevelTreeViewSelection: TreeViewSelection<
  string,
  GenericTreeNode<TreeBasicNode>,
  Record<string, string[] | undefined>,
  GroupedByLevelTreeSelectionOptions
> = ({treeData}, {value, setValue}, {disabled, entity, levels}) => {
  const [isAllSelected, setIsAllSelected] = useState(false);
  const levelKeys = useMemo(() => levels.map(level => level.key), [levels]);

  const selected = useMemo(() => levelKeys.flatMap(level => value[level]).filter(Boolean), [levelKeys, value]);
  const setSelected = useCallback(
    (newSelected: string[]) => {
      const groupedSelected = groupSelectedKeys(treeData, newSelected);
      const selectedByLevel = groupSelectedKeysByLevel(treeData, groupedSelected);

      if (selectedByLevel.at(0)?.length === treeData.length) {
        setIsAllSelected(true);

        return setValue(levelKeys.reduce((acc, level) => ({...acc, [level]: undefined}), {}));
      }

      setIsAllSelected(false);

      setValue(selectedByLevelToFilter(selectedByLevel, levelKeys as any));
    },
    [levelKeys, setValue, treeData]
  );

  return {
    isAllSelected,
    selected: useMemo(
      () => (isAllSelected ? treeData.map(node => node.key) : selected),
      [isAllSelected, selected, treeData]
    ),
    setSelected,
    isDisabled: disabled,
    selectionCount: useMemo(() => {
      return (
        <SelectedTooltip
          title={
            selected.length ? (
              <CounterTooltip
                total={`${entity} selected: ${selected.length}`}
                groups={levels.map(({key, label}) => ({
                  title: label,
                  count: value[key]?.length,
                }))}
              />
            ) : null
          }
        >
          {selected.length || 'All'}
        </SelectedTooltip>
      );
    }, [entity, levels, selected.length, value]),
  };
};
